import React from 'react';
import {
  Music,
  Apple,
  Cloud,
  Play,
  Disc,
  Youtube,
  Music2,
  Instagram,
  Twitter,
  Facebook,
  Globe
} from 'lucide-react';
import ColorPicker from '../ui/ColorPicker';
import './SocialBlockEditor.css';

// Platform configuration with lucide-react icons and labels
const PLATFORMS = {
  spotify: { label: 'Spotify', Icon: Music, color: '#1DB954' },
  applemusic: { label: 'Apple Music', Icon: Apple, color: '#FA243C' },
  soundcloud: { label: 'SoundCloud', Icon: Cloud, color: '#FF5500' },
  beatport: { label: 'Beatport', Icon: Play, color: '#01FF95' },
  bandcamp: { label: 'Bandcamp', Icon: Disc, color: '#629aa9' },
  youtube: { label: 'YouTube', Icon: Youtube, color: '#FF0000' },
  tiktok: { label: 'TikTok', Icon: Music2, color: '#000000' },
  instagram: { label: 'Instagram', Icon: Instagram, color: '#E4405F' },
  twitter: { label: 'Twitter/X', Icon: Twitter, color: '#1DA1F2' },
  facebook: { label: 'Facebook', Icon: Facebook, color: '#1877F2' },
  website: { label: 'Website', Icon: Globe, color: '#666666' },
};

const SocialBlockEditor = ({ block, onUpdate }) => {
  const handleChange = (field, value) => {
    onUpdate(block.id, {
      config: {
        ...block.config,
        [field]: value,
      },
    });
  };

  const handleStyleChange = (field, value) => {
    onUpdate(block.id, {
      styles: {
        ...block.styles,
        [field]: value,
      },
    });
  };

  const handleAddPlatform = () => {
    const newPlatform = {
      type: 'spotify',
      url: '',
      label: PLATFORMS.spotify.label,
    };
    handleChange('platforms', [...(block.config.platforms || []), newPlatform]);
  };

  const handleUpdatePlatform = (index, field, value) => {
    const updatedPlatforms = [...(block.config.platforms || [])];
    if (field === 'type') {
      // Update both type and label when platform changes
      updatedPlatforms[index] = {
        ...updatedPlatforms[index],
        type: value,
        label: PLATFORMS[value]?.label || value,
      };
    } else {
      updatedPlatforms[index] = { ...updatedPlatforms[index], [field]: value };
    }
    handleChange('platforms', updatedPlatforms);
  };

  const handleRemovePlatform = (index) => {
    const updatedPlatforms = (block.config.platforms || []).filter((_, i) => i !== index);
    handleChange('platforms', updatedPlatforms);
  };

  return (
    <div className="social-block-editor">
      <div className="editor-section">
        <h4 className="editor-section-title">Social Media Links</h4>

        <div className="editor-field">
          {(block.config.platforms || []).map((platform, index) => {
            const platformConfig = PLATFORMS[platform.type] || PLATFORMS.website;
            const IconComponent = platformConfig.Icon;
            return (
              <div key={index} className="platform-card">
                <div className="platform-header">
                  <div className="platform-info">
                    <span className="platform-icon" style={{ color: platformConfig.color }}>
                      <IconComponent size={24} strokeWidth={2} />
                    </span>
                    <span className="platform-label">{platformConfig.label}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemovePlatform(index)}
                    className="btn-remove"
                    title="Remove platform"
                  >
                    ✕
                  </button>
                </div>

                <div className="platform-fields">
                  <div className="editor-field">
                    <label>Platform</label>
                    <select
                      value={platform.type || 'spotify'}
                      onChange={(e) => handleUpdatePlatform(index, 'type', e.target.value)}
                      className="platform-select"
                    >
                      <optgroup label="Music Platforms">
                        <option value="spotify">Spotify</option>
                        <option value="applemusic">Apple Music</option>
                        <option value="soundcloud">SoundCloud</option>
                        <option value="beatport">Beatport</option>
                        <option value="bandcamp">Bandcamp</option>
                        <option value="youtube">YouTube Music</option>
                        <option value="tiktok">TikTok</option>
                      </optgroup>
                      <optgroup label="Social Media">
                        <option value="instagram">Instagram</option>
                        <option value="twitter">Twitter/X</option>
                        <option value="facebook">Facebook</option>
                      </optgroup>
                      <optgroup label="Other">
                        <option value="website">Website</option>
                      </optgroup>
                    </select>
                  </div>

                  <div className="editor-field">
                    <label>URL</label>
                    <input
                      type="url"
                      value={platform.url || ''}
                      onChange={(e) => handleUpdatePlatform(index, 'url', e.target.value)}
                      placeholder={`https://${platform.type}.com/...`}
                      className="url-input"
                    />
                  </div>
                </div>
              </div>
            );
          })}

          <button
            type="button"
            className="btn-add-platform"
            onClick={handleAddPlatform}
          >
            <span className="btn-icon">+</span>
            Add Social Platform
          </button>
        </div>
      </div>

      <div className="editor-section">
        <h4 className="editor-section-title">Styling</h4>

        <ColorPicker
          label="Background Color"
          color={block.styles.backgroundColor || '#000000'}
          onChange={(color) => handleStyleChange('backgroundColor', color)}
        />

        <div className="editor-field" style={{ marginTop: '1rem' }}>
          <label>Icon Size</label>
          <div className="slider-field">
            <input
              type="range"
              value={block.config.iconSize || 32}
              onChange={(e) => handleChange('iconSize', parseInt(e.target.value))}
              min="16"
              max="64"
              step="2"
              className="icon-size-slider"
            />
            <span className="slider-value">{block.config.iconSize || 32}px</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialBlockEditor;
